export interface SectionLock {
  move?: boolean;
  remove?: boolean;
  structure?: boolean;
}

interface SectionNodeSnapshot {
  id: string;
  path: string;
  sectionType: string;
  attrs: Record<string, unknown>;
  props: unknown;
}

export type SectionLockViolationCode =
  | "locked_section_inserted"
  | "locked_section_removed"
  | "locked_section_moved"
  | "locked_section_lock_changed"
  | "locked_section_structure_changed";

export interface SectionLockViolation {
  code: SectionLockViolationCode;
  sectionId: string;
  sectionType: string;
  field: string;
}

const CONTENT_PROP_PATHS: Readonly<
  Record<string, readonly (readonly (string | number)[])[]>
> = {
  hero: [
    ["eyebrow"],
    ["title"],
    ["description"],
    ["imageSrc"],
    ["imageAlt"],
    ["cta", "label"],
    ["cta", "href"],
  ],
  "rich-text": [["heading"], ["body"]],
  services: [
    ["heading"],
    ["description"],
    ["items", Number.NaN, "title"],
    ["items", Number.NaN, "description"],
    ["items", Number.NaN, "href"],
  ],
  faq: [
    ["heading"],
    ["items", Number.NaN, "question"],
    ["items", Number.NaN, "answer"],
  ],
  "cta-banner": [["title"], ["description"], ["cta", "label"], ["cta", "href"]],
  "blog-list": [["heading"], ["emptyMessage"]],
  "lead-form": [["heading"], ["description"], ["submitLabel"]],
  trust: [
    ["heading"],
    ["metrics", Number.NaN, "value"],
    ["metrics", Number.NaN, "unit"],
    ["metrics", Number.NaN, "label"],
    ["metrics", Number.NaN, "source"],
    ["credentials", Number.NaN, "name"],
    ["credentials", Number.NaN, "issuer"],
    ["credentials", Number.NaN, "kind"],
    ["credentials", Number.NaN, "image"],
    ["logos", Number.NaN, "src"],
    ["logos", Number.NaN, "alt"],
    ["logos", Number.NaN, "href"],
    ["disclaimer"],
  ],
  contact: [
    ["heading"],
    ["form", "enabled"],
    ["phone"],
    ["address"],
    ["hours", Number.NaN, "day"],
    ["hours", Number.NaN, "open"],
    ["hours", Number.NaN, "close"],
    ["hours", Number.NaN, "note"],
    ["mapEmbedUrl"],
  ],
  expertise: [
    ["heading"],
    ["description"],
    ["items", Number.NaN, "label"],
    ["items", Number.NaN, "kicker"],
    ["items", Number.NaN, "title"],
    ["items", Number.NaN, "body"],
    ["items", Number.NaN, "imageSrc"],
    ["items", Number.NaN, "imageAlt"],
    ["items", Number.NaN, "tags", Number.NaN],
    ["items", Number.NaN, "href"],
    ["items", Number.NaN, "ctaLabel"],
  ],
  spaces: [
    ["heading"],
    ["description"],
    ["spaces", Number.NaN, "label"],
    ["spaces", Number.NaN, "kicker"],
    ["spaces", Number.NaN, "title"],
    ["spaces", Number.NaN, "description"],
    ["spaces", Number.NaN, "imageSrc"],
    ["spaces", Number.NaN, "imageAlt"],
    ["spaces", Number.NaN, "tags", Number.NaN],
    ["disclaimer"],
  ],
  concerns: [
    ["heading"],
    ["description"],
    ["figureImage"],
    ["figureAlt"],
    ["regions", Number.NaN, "label"],
    ["regions", Number.NaN, "title"],
    ["regions", Number.NaN, "description"],
    ["regions", Number.NaN, "items", Number.NaN],
    ["regions", Number.NaN, "href"],
    ["regions", Number.NaN, "ctaLabel"],
    ["disclaimer"],
  ],
};

function valueEquals(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function collectChangedPaths(
  before: unknown,
  after: unknown,
  prefix: readonly (string | number)[] = [],
): Array<readonly (string | number)[]> {
  if (valueEquals(before, after)) return [];
  if (Array.isArray(before) && Array.isArray(after)) {
    if (before.length !== after.length) return [prefix];
    return before.flatMap((item, index) =>
      collectChangedPaths(item, after[index], [...prefix, index]),
    );
  }
  if ((isRecord(before) || before == null) && (isRecord(after) || after == null)) {
    const beforeRecord = isRecord(before) ? before : {};
    const afterRecord = isRecord(after) ? after : {};
    const keys = new Set([...Object.keys(beforeRecord), ...Object.keys(afterRecord)]);
    if (keys.size === 0) return [prefix];
    return [...keys].flatMap((key) =>
      collectChangedPaths(beforeRecord[key], afterRecord[key], [...prefix, key]),
    );
  }
  return [prefix];
}

function contentPathMatches(
  pattern: readonly (string | number)[],
  path: readonly (string | number)[],
): boolean {
  if (pattern.length !== path.length) return false;
  return pattern.every((segment, index) => {
    const actual = path[index];
    return Number.isNaN(segment)
      ? typeof actual === "number"
      : segment === actual;
  });
}

export function isSectionContentPropPath(
  sectionType: string,
  path: readonly (string | number)[],
): boolean {
  const patterns = CONTENT_PROP_PATHS[sectionType] ?? [];
  return patterns.some((pattern) => contentPathMatches(pattern, path));
}

function collectSectionSnapshots(doc: unknown): SectionNodeSnapshot[] {
  const sections: SectionNodeSnapshot[] = [];
  function visit(node: unknown, path: string): void {
    if (!isRecord(node)) return;
    if (node.type === "section") {
      const attrs = isRecord(node.attrs) ? node.attrs : {};
      sections.push({
        id: typeof attrs._id === "string" ? attrs._id : "",
        path,
        sectionType: typeof attrs.sectionType === "string" ? attrs.sectionType : "",
        attrs,
        props: attrs.props ?? {},
      });
    }
    if (Array.isArray(node.content)) {
      node.content.forEach((child, index) => visit(child, `${path}.${index}`));
    }
  }
  visit(doc, "doc");
  return sections;
}

/** 고객 콘텐츠 저장 경계. 기존 lock 플래그와 무관하게 제작 구조를 보호한다. */
export function findSectionLockViolations(
  beforeDoc: unknown,
  afterDoc: unknown,
): SectionLockViolation[] {
  const before = collectSectionSnapshots(beforeDoc);
  const after = collectSectionSnapshots(afterDoc);
  const afterById = new Map(after.map((section) => [section.id, section]));
  const beforeById = new Map(before.map((section) => [section.id, section]));
  const violations: SectionLockViolation[] = [];

  // ID 누락·중복은 동일 섹션 비교를 우회할 수 있으므로 실패로 처리한다.
  for (const sections of [before, after]) {
    const ids = new Set<string>();
    for (const section of sections) {
      if (!section.id || ids.has(section.id)) {
        violations.push({
          code: "locked_section_structure_changed",
          sectionId: section.id,
          sectionType: section.sectionType,
          field: "_id",
        });
      }
      ids.add(section.id);
    }
  }

  for (const section of before) {
    const next = afterById.get(section.id);
    if (!next) {
      violations.push({
        code: "locked_section_removed",
        sectionId: section.id,
        sectionType: section.sectionType,
        field: "(node)",
      });
      continue;
    }

    if (section.path !== next.path) {
      violations.push({
        code: "locked_section_moved",
        sectionId: section.id,
        sectionType: section.sectionType,
        field: "(index)",
      });
    }

    const fields = new Set([
      ...Object.keys(section.attrs),
      ...Object.keys(next.attrs),
    ]);
    fields.delete("props");
    for (const field of fields) {
      if (!valueEquals(section.attrs[field], next.attrs[field])) {
        violations.push({
          code: field === "lock"
            ? "locked_section_lock_changed"
            : "locked_section_structure_changed",
          sectionId: section.id,
          sectionType: section.sectionType,
          field,
        });
      }
    }

    for (const path of collectChangedPaths(section.props, next.props)) {
      if (!isSectionContentPropPath(section.sectionType, path)) {
        violations.push({
          code: "locked_section_structure_changed",
          sectionId: section.id,
          sectionType: section.sectionType,
          field: `props.${path.join(".") || "(root)"}`,
        });
      }
    }
  }

  for (const section of after) {
    if (beforeById.has(section.id)) continue;
    violations.push({
      code: "locked_section_inserted",
      sectionId: section.id,
      sectionType: section.sectionType,
      field: "(node)",
    });
  }

  return violations;
}

export function formatSectionLockViolations(
  violations: readonly SectionLockViolation[],
): string {
  return violations
    .map((violation) => {
      const label = `${violation.sectionType || "section"} 섹션(${violation.sectionId})`;
      if (violation.code === "locked_section_inserted") return `${label} 추가`;
      if (violation.code === "locked_section_removed") return `${label} 삭제`;
      if (violation.code === "locked_section_moved") return `${label} 이동`;
      if (violation.code === "locked_section_lock_changed") {
        return `${label} 잠금 변경`;
      }
      return `${label} 구조 변경(${violation.field})`;
    })
    .join(" / ");
}
